/**
 * @module app.cm
 */

import { autoinject } from 'aurelia-framework';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { INotesConfig, ITasksConfig, IDocumentsConfig, IAssessmentsConfig, IContactLogConfig, IQueuesConfig } from 'app/core/models/essette-interfaces';
import { openGlobalModal, IModalData, IDialogOptions } from 'app/shared/elements/ess-global-modal';
import { IPrintable } from 'app/shared/lib/printing';
import * as CM from 'app/cm/models/essette-api';
import { guid } from 'app/shared/lib/guid';


export interface IHouseholdCaseSummaryModelData {
    householdCaseId: string;
    readonly?: boolean;
}

@autoinject()
export class HouseholdCaseSummary extends ComponentBase implements IPrintable {

    ////****************** Fields and Properties ************************************////
    protected householdCaseId: string | null = null;

    protected readonly: boolean = false;
    protected canViewCareTeam: boolean = false;
    protected hasHouseholdCaseRoute = false;
    protected showAssignmentHistory: boolean = true;

    protected notesConfig: INotesConfig = {
        serviceRootUrl: 'cm/cases',
        targetType: 'CM.Case',
        moduleCode: 'CM',
        excludeSystem: false,
        includeRollup: true,
        flagCodes: ['CM.CaseFile'],
        rollupConfig: {
            serviceRootUrl: 'cm/households/cases'
        }
    }

    protected assessmentsConfig: IAssessmentsConfig = {
        serviceRootUrl: 'cm/cases',
        targetType: 'CM.Case',
        moduleCode: 'CM',
        rollupConfig: {
            serviceRootUrl: 'cm/households/cases'
        }
    };

    protected tasksConfig: ITasksConfig = {
        serviceRootUrl: 'cm/cases',
        targetType: 'CM.Case',
        primaryTargetType: 'CM.CaseTask',
        labelKey: 'core:task.$',
        allowDuplicates: false,
        rollupConfig: {
            serviceRootUrl: 'cm/households/cases'
        }
    };

    protected documentsConfig: IDocumentsConfig = {
        serviceRootUrl: 'cm/cases',
        targetType: 'CM.Case',
        hasTasks: true
    };

    protected contactLogConfig: IContactLogConfig = {
        serviceRootUrl: 'cm/households/cases',
        targetType: 'CM.Case'
    };

    protected id: guid = guid();

    ////****************** Element References ***************************************////

    protected $carePlan!: IPrintable;

    ////****************** Lifecycle events (in order of execution) *****************////

    constructor(...rest: any[]) {
        super(...rest);
    }

    protected async activate(model: IHouseholdCaseSummaryModelData): Promise<void> {
        if(!model) { return; }

        await this.checkPermissions();
        if(this.env.application.code === 'Portal.Providers') {
            this.documentsConfig.categoryFlagCodes = ['PP.View'];
            model.readonly = true;
            this.showAssignmentHistory = false;
        }

        this.readonly = model.readonly ?? this.readonly;

        this.householdCaseId = model.householdCaseId;
    }

    ////****************** Events ***************************************************////

    ////****************** Functions ************************************************////
    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.hasHouseholdCaseRoute = this.accountContext.hasRouteAccess('cm.household-case');
        this.canViewCareTeam = this.accountContext.hasRouteAccess('cm.case.careteam');
    }

    public async prepareForPrinting(): Promise<void> {
        // prepare care plan for printing
        if(this.$carePlan && this.$carePlan.prepareForPrinting) {
            this.$carePlan.prepareForPrinting();
        }
    }

    ////****************** Service Calls ********************************************////

}

/**
 * Opens the household case summary modal with the global dialog
 * @param $case The case information to use
 */
export function openHouseholdCaseSummary($householdCase: CM.HouseholdCase, hasHouseholdCaseRoute: boolean = false, readonly: boolean = false): void {
    if(!$householdCase || !$householdCase.id) {
        throw new Error('Household case is missing or invalid');
    }

    const dialogOptions: IDialogOptions = {
        width: '10',
        height: '11',
        helpTopic: 'cm.household-case.summary',
        title: {
            key: 'cm:routes.household-case.summary.$title',
            replace: { number: $householdCase.number }
        },
        print: true,
        popout: {
            route: 'cm.household-case.summary',
            params: { householdCaseId: $householdCase.id }
        }
    };

    const modelData: IHouseholdCaseSummaryModelData = {
        householdCaseId: $householdCase.id,
        readonly: readonly
    };

    const modalData: IModalData = {
        componentId: 'app/cm/components/summary/household-case-summary',
        modelData: modelData,
        dialogOptions: hasHouseholdCaseRoute ? {
            ...dialogOptions,
            openRecord: {
                route: 'cm.household-case',
                params: {
                    householdCaseId: $householdCase.id,
                },
                tooltipDesc: {
                    key: 'ui:actions.goTo',
                    replace: {
                        what: '$t(cm:householdCase.$)',
                        item: $householdCase.number
                    }
                }
            }
        }
            :
            dialogOptions
    };

    openGlobalModal(modalData);
}
